<template>
  <model-form
    ref="modelForm"
    :model="membershipTier"
    :title="`${membershipTier.membershipTypeId ? `Edit ${membershipTier.name} for ${membershipTier.membershipType.name}` : `Add Tier to ${membershipType.name}`}`"
    :submodel="true"
    modal-type="modal"
    :embedded-in-model-table="false"
    :show-and-close="false"
    :close-on-save="true"
    @add-model="membershipTier => $emit('add-model', membershipTier)"
  >
    <div class="column">
      <div class="row q-col-gutter-sm items-start">
        <model-form-input
          field="name"
          class="col-8"
          label="Name"
          :rules="[val => val !== null && val !== '' || 'Field is required']"
        />
        <div class="col-4">
          <color-picker
            color-name="custom"
            :color-hex="membershipTier.color"
            :disable="membershipTier.isTranslation"
            @update:model-value="value => membershipTier.color = value"
          />
        </div>
        <q-select
          v-model="membershipTier.descriptionType"
          class="col-12"
          label="Use Description As"
          :options="$mh.asOptions(MEMBERSHIP_TIER_DESCRIPTION_TYPES.ALL)"
          :rules="[val => val !== null || 'Field is required']"
          map-options
          emit-value
          bottom-slots
        >
          <q-tooltip>
            The membership type selector auto-generated description currently only advertises unconditional ticket vouchers, discounts, and benefits.
            <br>Replace the description to advertise all membership benefits.
          </q-tooltip>
        </q-select>
        <q-editor
          v-model="membershipTier.description"
          class="col-12 q-ml-xs"
          placeholder="Description"
          :toolbar="[
            [
              {
                label: $q.lang.editor.align,
                icon: $q.iconSet.editor.align,
                fixedLabel: true,
                list: 'only-icons',
                options: ['left', 'center', 'right', 'justify']
              }
            ],
            ['bold', 'italic', 'strike', 'underline', 'subscript', 'superscript'],
            ['token', 'hr', 'link', 'custom_btn'],
            ['print', 'fullscreen'],
            [
              {
                label: $q.lang.editor.formatting,
                icon: $q.iconSet.editor.formatting,
                list: 'no-icons',
                options: [
                  'p',
                  'h1',
                  'h2',
                  'h3',
                  'h4',
                  'h5',
                  'h6',
                  'code'
                ]
              },
              {
                label: $q.lang.editor.fontSize,
                icon: $q.iconSet.editor.fontSize,
                fixedLabel: true,
                fixedIcon: true,
                list: 'no-icons',
                options: [
                  'size-1',
                  'size-2',
                  'size-3',
                  'size-4',
                  'size-5',
                  'size-6',
                  'size-7'
                ]
              },
              {
                label: $q.lang.editor.defaultFont,
                icon: $q.iconSet.editor.font,
                fixedIcon: true,
                list: 'no-icons',
                options: [
                  'default_font',
                  'arial',
                  'arial_black',
                  'comic_sans',
                  'courier_new',
                  'impact',
                  'lucida_grande',
                  'times_new_roman',
                  'verdana'
                ]
              },
              'removeFormat'
            ],
            ['quote', 'unordered', 'ordered', 'outdent', 'indent'],

            ['undo', 'redo'],
            ['viewsource']
          ]"
          :fonts="{
            arial: 'Arial',
            arial_black: 'Arial Black',
            comic_sans: 'Comic Sans MS',
            courier_new: 'Courier New',
            impact: 'Impact',
            lucida_grande: 'Lucida Grande',
            times_new_roman: 'Times New Roman',
            verdana: 'Verdana'
          }"
        />
        <q-select
          v-model="membershipTier.conditions"
          class="col-12"
          label="Conditions"
          :options="$mh.asOptions(MEMBERSHIP_TIER_CONDITIONS.ALL)"
          map-options
          emit-value
          multiple
          :rules="[val => membershipTier.conditions.length === 0 || $_.filter(membershipType.membershipTiers.data, data => { return membershipTier.id !== data.id && data.conditions.length === 0 }).length > 0 || 'An unconditional tier is required']"
          bottom-slots
        >
          <template #selected-item="scope">
            <q-chip
              removable
              dense
              :tabindex="scope.tabindex"
              :label="scope.opt.label"
              @remove="scope.removeAtIndex(scope.index)"
            />
          </template>
          <template #option="scope">
            <q-item
              v-bind="scope.itemProps"
              v-on="scope.itemEvents"
            >
              <q-item-section>
                <div class="row items-center justify-between q-gutter-x-sm">
                  <span>{{ scope.opt.label }}</span>
                  <q-icon
                    name="info"
                    size="sm"
                  >
                    <q-tooltip>
                      {{ MEMBERSHIP_TIER_CONDITIONS_TOOLTIPS[scope.opt.value] }}
                    </q-tooltip>
                  </q-icon>
                </div>
              </q-item-section>
            </q-item>
          </template>
          <q-tooltip>
            Set conditions to be met for members to attain the selected tier. If multiple conditions are selected, all must be met to attain the tier.
          </q-tooltip>
        </q-select>
        <!-- Condition Requirements -->
        <q-input
          v-if="$_.includes(membershipTier.conditions, MEMBERSHIP_TIER_CONDITIONS.VISITS)"
          v-model.number="membershipTier.conditionsRequirements.visits"
          class="col-6"
          label="Required Visits"
          type="number"
          step="1"
          min="1"
          :rules="[val => !!val || 'Field is required']"
          bottom-slots
        />
        <q-input
          v-if="$_.includes(membershipTier.conditions, MEMBERSHIP_TIER_CONDITIONS.TICKETS)"
          v-model.number="membershipTier.conditionsRequirements.tickets"
          class="col-6"
          label="Required Tickets"
          type="number"
          step="1"
          min="1"
          :rules="[val => !!val || 'Field is required']"
          bottom-slots
        />
        <q-input
          v-if="$_.includes(membershipTier.conditions, MEMBERSHIP_TIER_CONDITIONS.MOVIES)"
          v-model.number="membershipTier.conditionsRequirements.movies"
          class="col-6"
          label="Required Movies"
          type="number"
          step="1"
          min="1"
          :rules="[val => !!val || 'Field is required']"
          bottom-slots
        />
        <q-input
          v-if="$_.includes(membershipTier.conditions, MEMBERSHIP_TIER_CONDITIONS.POINTS)"
          v-model.number="membershipTier.conditionsRequirements.points"
          class="col-6"
          label="Required Points Earned"
          type="number"
          step="1"
          min="1"
          :rules="[val => !$_.isNil(val) || 'Field is required']"
          bottom-slots
        />
        <q-input
          v-if="$_.includes(membershipTier.conditions, MEMBERSHIP_TIER_CONDITIONS.POINTS_BALANCE )"
          v-model.number="membershipTier.conditionsRequirements.points_balance"
          class="col-6"
          label="Required Points Balance"
          type="number"
          step="1"
          min="1"
          :rules="[val => !$_.isNil(val) || 'Field is required']"
          bottom-slots
        />
      </div>
    </div>
  </model-form>
</template>

<script>
import { MEMBERSHIP_TIER_CONDITIONS, MEMBERSHIP_TIER_DESCRIPTION_TYPES } from 'src/models/membership-type/MembershipTier'
import MembershipType from 'src/models/membership-type/MembershipType'
import ColorPicker from 'src/modules/admin/components/ColorPicker'
import ModelForm from 'src/modules/admin/components/ModelForm/ModelForm'

const MEMBERSHIP_TIER_CONDITIONS_TOOLTIPS = {
  visits: 'Require members complete a given number of orders on distinct days.',
  tickets: 'Require members purchase a given number of tickets.',
  movies: 'Require members watch a given number of distinct movies.',
  spend: 'Require members spend a given amount.',
  longevity: 'Require members maintain their membership for a given number of months or years.',
  points: 'Require members earn a given number of points that are not expired.',
  points_balance: 'Require members have a given number of points that are not expired or used.'
}

export default {
  name: 'MembershipTierForm',
  components: {
    ColorPicker,
    ModelForm
  },
  configuration: ['currentCircuit'],
  context: ['currentUser'],
  props: {
    membershipType: {
      type: MembershipType,
      required: true
    }
  },
  models: {
    membershipTier: {
      membershipType: 'default'
    }
  },
  emits: ['add-model'],
  created () {
    _.extend(this, { MEMBERSHIP_TIER_CONDITIONS_TOOLTIPS, MEMBERSHIP_TIER_CONDITIONS, MEMBERSHIP_TIER_DESCRIPTION_TYPES })
  }
}

</script>
